'use client';

import { useEffect, useMemo, useRef } from 'react';
import {
  trackFormClosed as sendTrackFormClosed,
  trackFormFailedSubmit as sendTrackFormFailedSubmit,
  trackFormFailedToDisplay as sendTrackFormFailedToDisplay,
  trackFormMouseover as sendTrackFormMouseover,
  trackFormOpen as sendTrackFormOpen,
  trackFormOpenEmailPrefilled as sendTrackFormOpenEmailPrefilled,
  trackFormStartedEmailBoxClick as sendTrackFormStartedEmailBoxClick,
  trackFormStartedEmailBoxType as sendTrackFormStartedEmailBoxType,
  trackFormSubmitted as sendTrackFormSubmitted,
  trackMicroDataQuestionDisplayed as sendTrackMicroDataQuestionDisplayed,
  trackMicroDataQuestionFailedSubmit as sendTrackMicroDataQuestionFailedSubmit,
  trackMicroDataQuestionSubmitted as sendTrackMicroDataQuestionSubmitted,
} from '../../browser/analytics';
import type { AnalyticsFormName, FormCloseDetail } from '../../core';
import { recordFormExposeAction } from '../../server/recordFormExposeAction';

interface FormExposeContext {
  formTemplate: string;
  formKind: 'inline' | 'modal' | 'multi-field-inline' | 'vendor-claim';
  brand?: string;
  placement?: string;
  page_type?: string;
  page_path?: string;
  eloqua_campaign?: string;
}

export function useFormAnalytics(
  formId: string,
  formName: AnalyticsFormName = 'inline',
  resetKey?: boolean | number | string
) {
  const trackedEvents = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (resetKey !== undefined) {
      trackedEvents.current.clear();
    }
  }, [resetKey]);

  return useMemo(() => {
    const params = { form_id: formId, form_name: formName };

    // Core tracker that ensures each unique key is only logged once per session
    const track = (key: string, action: () => void) => {
      if (!trackedEvents.current.has(key)) {
        action();
        trackedEvents.current.add(key);
      }
    };

    return {
      trackFormFailedToDisplay: () =>
        track('failed-to-display', () => sendTrackFormFailedToDisplay(params)),

      trackFormOpen: () => track('form-open', () => sendTrackFormOpen(params)),

      /**
       * Record a `bx_forms.expose.recorded` server log on form-open. Fires
       * once per session via the same guard as `trackFormOpen` so the DD
       * submit:expose ratio reflects unique form openings, not raw call
       * count. Best-effort — failures don't block the user-facing flow.
       */
      trackFormExposeServer: (ctx: FormExposeContext) =>
        track('form-expose-server', () => {
          recordFormExposeAction({ formId, ...ctx }).catch(() => {});
        }),

      trackFormPrefilled: () =>
        track('form-prefilled', () => sendTrackFormOpenEmailPrefilled(params)),

      trackFormStarted: (detail: string) =>
        track(`started-${detail}`, () => {
          if (detail === 'email-box-click') {
            sendTrackFormStartedEmailBoxClick(params);
          } else if (detail === 'email-box-type') {
            sendTrackFormStartedEmailBoxType(params);
          }
        }),

      trackFormSubmitted: (ctaText?: string) =>
        track('form-submitted', () =>
          sendTrackFormSubmitted({ ...params, cta_text: ctaText })
        ),

      trackFormFailed: () =>
        track('form-failed-submit', () => sendTrackFormFailedSubmit(params)),

      trackMicroDataQuestionDisplayed: () =>
        track('micro-data-question-displayed', () =>
          sendTrackMicroDataQuestionDisplayed(params)
        ),

      trackMicroDataQuestionSubmitted: () =>
        track('micro-data-question-submitted', () =>
          sendTrackMicroDataQuestionSubmitted(params)
        ),

      trackMicroDataQuestionFailed: () =>
        track('micro-data-question-failed-submit', () =>
          sendTrackMicroDataQuestionFailedSubmit(params)
        ),

      trackFormClosed: (step: 1 | 2, detail: FormCloseDetail = 'close-page') =>
        track('form-closed', () =>
          sendTrackFormClosed({
            ...params,
            form_step_number: step,
            form_step_details: detail,
          })
        ),

      trackFormMouseover: () =>
        track('mouseover', () => sendTrackFormMouseover(params)),
    };
  }, [formId, formName]);
}
